/**
 * Build a slide thumbnail URL.
 *
 * Always returns either a cached blob URL (fed via the batch endpoint),
 * the legacy pre-signed SAS URL embedded in older list/search responses,
 * or `null` while a fetch is in flight.
 *
 * Uncached lookups join the shared 50 ms debounce window in
 * `use-thumbnail-lazy-request`, so a deck expanding with N visible cards
 * fires ONE batch HTTP call with N ids — not N single-id batches.
 *
 * `onImgError` is still exposed so callers can surface broken-image
 * state when the eventual URL fails to render.
 */

import { useEffect, useState } from "react"

import { requestThumbnail } from "./use-thumbnail-lazy-request"
import { useThumbnailCache } from "./use-thumbnail-prefetch"

export function useSlideThumbnail(
  slideId: string | undefined,
  legacyPrefetchedUrl?: string | null
) {
  const cachedUrl = useThumbnailCache((s) => (slideId ? s.urls.get(slideId) : undefined))
  const isPending = useThumbnailCache((s) => (slideId ? s.inFlight.has(slideId) : false))
  const hasFailed = useThumbnailCache((s) => (slideId ? s.failed.has(slideId) : false))

  useEffect(() => {
    if (!slideId) return
    if (cachedUrl || legacyPrefetchedUrl || isPending || hasFailed) return
    // Coalesce — multiple simultaneous mounts share one HTTP batch.
    requestThumbnail(slideId)
  }, [slideId, cachedUrl, legacyPrefetchedUrl, isPending, hasFailed])

  const url = cachedUrl ?? legacyPrefetchedUrl ?? null
  const [imgError, setImgError] = useState(false)
  const onImgError = () => setImgError(true)
  return { url, error: hasFailed || imgError, onImgError }
}

/** No-op kept for back-compat with existing test setups. */
export function clearThumbnailCache(): void {}
